/**
 * Console → Gifts & ledger → **Pledges** (GH-885; owner, 2026-10-04).
 *
 *   GET    /v1/organizations/:organizationId/pledges?status=…                    donation.view_masked
 *   PUT    /v1/organizations/:organizationId/pledges/:pledgeId/request-plan      payment_request.send
 *   DELETE /v1/organizations/:organizationId/pledges/:pledgeId/request-plan      payment_request.send
 *   POST   /v1/jobs/run-pledge-request-plans                                     operator secret
 *
 * A pledge is a promise and is never received money: no figure here is a received figure. "Given
 * toward" is what real, paid gifts have already given toward a promise — those gifts are counted in
 * Received on their own, and nothing here adds a promise to them.
 *
 * Making one payment request from a pledge is GH-845's own `POST …/payment-requests` with a
 * `pledgeId`; this file adds only the list and the standing instruction ("ask automatically").
 *
 * The lists are spelled out rather than imported from `@givehere/domain`, which this package keeps
 * out of the browser bundles; `services/api` pins them equal in a test.
 */
import { z } from "zod";
import { moneySchema } from "./v1.js";
export const ORGANIZATION_PLEDGE_STATUSES = ["open", "partly_given", "kept", "lapsed"];
export const ORGANIZATION_PLEDGE_FILTERS = ["all", ...ORGANIZATION_PLEDGE_STATUSES];
export const PLEDGE_REQUEST_CADENCES_WIRE = ["weekly", "biweekly", "monthly"];
export const PLEDGE_REQUEST_PLAN_STATUSES_WIRE = ["active", "stopped", "finished"];
export const PLEDGE_REQUEST_RUN_OUTCOMES_WIRE = [
    "requested",
    "open_request",
    "no_email",
    "finished",
    "organization_unavailable",
];
export const organizationPledgeListQuerySchema = z.object({
    status: z.enum(ORGANIZATION_PLEDGE_FILTERS).default("all"),
});
export const pledgeRequestPlanSchema = z.object({
    cadence: z.enum(PLEDGE_REQUEST_CADENCES_WIRE),
    /** Null asks for everything still promised each time. */
    installment: moneySchema.nullable(),
    /** Days each request gives the person before its date (3–60). */
    giveByDays: z.number().int(),
    /** The next date the plan will ask, `YYYY-MM-DD` (UTC). */
    nextRunOn: z.string(),
    status: z.enum(PLEDGE_REQUEST_PLAN_STATUSES_WIRE),
    /** Why it stopped or finished, in plain words; null while active. */
    endedReason: z.string().nullable(),
});
export const organizationPledgeRowSchema = z.object({
    id: z.string().uuid(),
    status: z.enum(ORGANIZATION_PLEDGE_STATUSES),
    /** The event the promise was made at; the general fund reads as the organization itself. */
    eventId: z.string().uuid(),
    eventName: z.string(),
    isGeneralFund: z.boolean(),
    designationName: z.string().nullable(),
    /** "Amina Rahman", or "Anonymous donor" for a donor hidden from organizations (DEC-049). */
    donorLabel: z.string(),
    promised: moneySchema,
    /** What paid gifts have given toward it. Already counted in Received; never added again here. */
    givenToward: moneySchema,
    stillPromised: moneySchema,
    pledgedAt: z.string(),
    /** Whether a request can be emailed without anybody typing an address. */
    hasEmail: z.boolean(),
    /** The newest request for this pledge that is still asking. */
    openRequest: z
        .object({
        id: z.string().uuid(),
        status: z.string(),
        amount: moneySchema,
        paid: moneySchema,
        dueOn: z.string().nullable(),
    })
        .nullable(),
    requestCount: z.number().int().nonnegative(),
    plan: pledgeRequestPlanSchema.nullable(),
    lastRun: z
        .object({ runOn: z.string(), outcome: z.enum(PLEDGE_REQUEST_RUN_OUTCOMES_WIRE) })
        .nullable(),
});
export const organizationPledgeListResponseSchema = z.object({
    pledges: z.array(organizationPledgeRowSchema),
    totals: z.object({
        openCount: z.number().int().nonnegative(),
        /** Promised and not yet given, across open and partly given pledges. Not money received. */
        stillPromised: moneySchema,
        /** Given toward pledges by paid gifts — already inside Received, shown here for context. */
        givenToward: moneySchema,
        keptCount: z.number().int().nonnegative(),
        lapsedCount: z.number().int().nonnegative(),
        activePlanCount: z.number().int().nonnegative(),
    }),
    truncated: z.boolean(),
    /** `payment_request.send`: whether this caller may ask (a request or a plan). */
    canAct: z.boolean(),
    /** The platform's reminder days before each request's date (GH-849), e.g. [7, 1]. */
    reminderDaysBefore: z.array(z.number().int()),
});
export const savePledgeRequestPlanSchema = z.object({
    cadence: z.enum(PLEDGE_REQUEST_CADENCES_WIRE),
    /** Minor units; omitted or null asks for everything still promised each time. */
    installmentMinor: z
        .string()
        .regex(/^[1-9][0-9]{0,9}$/)
        .nullable()
        .default(null),
    giveByDays: z.number().int().min(3).max(60).default(14),
    /** `YYYY-MM-DD`; omitted starts today (and asks at once). */
    startOn: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
/** The answer to turning a plan on or off: the pledge's row as it now stands. */
export const pledgeRequestPlanResponseSchema = z.object({
    pledge: organizationPledgeRowSchema,
    /** What the first run did, when the plan starts today; null otherwise. */
    firstRun: z.enum(PLEDGE_REQUEST_RUN_OUTCOMES_WIRE).nullable(),
    /** Plain words about the first email, when one was attempted. */
    delivery: z.object({ status: z.string(), message: z.string() }).nullable(),
});
export const pledgeRequestPlanRunResultSchema = z.object({
    examined: z.number().int().nonnegative(),
    requested: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    finished: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
//# sourceMappingURL=organization-pledges.js.map